import React, { useState } from "react";
import toast from "react-hot-toast";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import AnonSilhouetteAvatar from "./AnonSilhouetteAvatar";
import Avatar from "../common/Avatar";
import { lastMinuteService } from "../../services/lastMinuteService";
import { formatMarketValueExact, formatMarketValueRange, urgencyMeta } from "../../utils/lastMinute";

// Agent → agent "I have an opportunity" message-request composer — V2 rebuild
// from artboards 9a (composer) + 9b (sent) + 8i (mobile bottom sheet). The
// intent is a FIXED, non-editable "Player Opportunity" pill — set by context,
// no dropdown. Sending routes to the listing agent, never the player; the
// recipient Accepts (→ chat) or Ignores (silent). Behavior unchanged.
const OpportunityComposer = ({ open, onClose, listing }) => {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open || !listing) return null;

  const agent = listing.agent || {};
  const named = !!listing.is_named;
  const subject = named ? listing.player_name || "Player" : [listing.position, listing.age].filter(Boolean).join(", ");
  const mv = named ? formatMarketValueExact(listing.market_value) : formatMarketValueRange(listing.market_value);
  const contextLine = ["Last Minute", urgencyMeta(listing.urgency_class).label, listing.position, listing.age, mv]
    .filter((v) => v !== null && v !== undefined && v !== "")
    .join(" · ");

  // The listing agent's Agent id (needed as the request recipient). Exposed on the
  // feed serializer's agent block as `agent_id`.
  const recipientAgentId = agent.agent_id;

  const send = async () => {
    if (sending) return;
    if (!recipientAgentId) {
      toast.error("Could not resolve the listing agent. Please try again later.");
      return;
    }
    setSending(true);
    const res = await lastMinuteService.sendOpportunity({
      listingId: listing.id,
      recipientAgentId,
      message,
    });
    setSending(false);
    if (res.success) setSent(true);
    else toast.error(res.error || "Could not send the request.");
  };

  // Fixed intent pill (9a) — dot 5px, no dropdown ever.
  const intentPill = (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E9F2FF] border border-[#BDD9FF] px-3 py-[5px] text-[12px] font-semibold text-[#1466D8]">
      <span className="w-[5px] h-[5px] rounded-full bg-[#2283FF]" />
      Player Opportunity
    </span>
  );

  // About-this-listing context box (9a / 8i).
  const aboutBox = (
    <div className="bg-[#F8FAFC] border border-[#E4E9F1] rounded-xl px-3 py-2.5 flex items-center gap-2.5">
      {named ? <Avatar src={null} alt={subject} fallbackText={subject} size={36} className="flex-none" /> : <AnonSilhouetteAvatar size={36} />}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold text-[#14263D] truncate">{subject}</p>
        <p className="text-[11.5px] text-[#8A98AC] truncate">{contextLine}</p>
      </div>
      {!isMobile && (
        <span className="inline-flex items-center rounded-full bg-[#EDF1F6] px-2.5 py-[3px] text-[10.5px] font-semibold text-[#71829A] flex-shrink-0">
          via Last Minute
        </span>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[rgba(16,26,40,0.44)] p-0 sm:p-4">
      <div
        className={`bg-white w-full flex flex-col shadow-[0_32px_64px_-16px_rgba(16,24,40,0.35)] ${
          sent
            ? "sm:max-w-[400px] rounded-t-[22px] sm:rounded-[18px]"
            : "sm:max-w-[560px] rounded-t-[22px] sm:rounded-[18px]"
        } max-h-[92vh]`}
      >
        {sent ? (
          // 9b — request sent
          <div className="flex flex-col items-center text-center px-7 py-9 gap-2.5">
            <div className="w-[54px] h-[54px] rounded-full bg-[#E6F6EC] flex items-center justify-center mb-1">
              <span className="text-[#0E7C3C] text-[22px] font-bold" aria-hidden="true">✓</span>
            </div>
            <h3 className="text-[17px] font-bold text-[#14263D]">Request sent</h3>
            <p className="text-[13px] text-[#5A6B82] leading-[1.55] max-w-[300px]">
              You can chat with {agent.name || "the agent"} as soon as they accept your request.
            </p>
            <div className="mt-1.5 flex items-center gap-2 rounded-[10px] bg-[#F8FAFC] border border-[#E4E9F1] px-3 py-2">
              {named ? <Avatar src={null} alt={subject} fallbackText={subject} size={24} className="flex-none" /> : <AnonSilhouetteAvatar size={24} />}
              <span className="text-[12px] font-semibold text-[#33475E]">{subject}</span>
              <span className="text-[11.5px] text-[#8A98AC]">· Player Opportunity</span>
              <span className="inline-flex items-center rounded-full bg-[#EDF1F6] px-2 py-0.5 text-[10px] font-semibold text-[#71829A]">
                via Last Minute
              </span>
            </div>
            <button
              onClick={onClose}
              className="mt-3 px-9 py-2.5 rounded-[10px] bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0]"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {isMobile && (
              <div className="flex justify-center pt-2.5" aria-hidden="true">
                <span className="w-[38px] h-[4.5px] rounded-full bg-[#D5DDE8]" />
              </div>
            )}
            <div className="px-[18px] sm:px-6 pt-3 sm:pt-6">
              <div className="flex items-center gap-2">
                <h2 className="text-[17px] font-bold text-[#14263D]">Contact {agent.name || "the agent"}</h2>
                {!isMobile && (
                  <button onClick={onClose} className="ml-auto text-[#98A5B8] text-[14px] leading-none p-1 -m-1 hover:text-[#14263D]" aria-label="Close">
                    ✕
                  </button>
                )}
              </div>
              <p className="text-[12.5px] text-[#5A6B82] mt-[3px]">
                {isMobile ? "Sent as a message request." : "This is a message request. You can chat once they accept."}
              </p>
            </div>

            <div className="px-[18px] sm:px-6 py-4 flex flex-col gap-4 overflow-y-auto">
              {/* Fixed intent (no dropdown) */}
              <div className="flex items-center gap-2 flex-wrap">
                {intentPill}
                <span className="text-[11px] text-[#98A5B8]">
                  {isMobile ? "Fixed by context" : "Fixed by context, set automatically on Last Minute listings."}
                </span>
              </div>

              {/* About this listing */}
              <div>
                <p className="text-[12px] font-semibold text-[#33475E] mb-2">About this listing</p>
                {aboutBox}
              </div>

              {/* Message */}
              <div>
                <p className="text-[12px] font-semibold text-[#33475E] mb-2">Message</p>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                  rows={4}
                  placeholder="I have a club looking for exactly this profile; the band fits. Open to a quick call this week?"
                  className="w-full border border-[#DFE6EF] rounded-xl px-3.5 py-3 text-[13px] text-[#243B55] leading-[1.55] min-h-[84px] resize-none focus:outline-none focus:border-[#2283FF] focus:ring-[3px] focus:ring-[#2283FF]/10"
                />
              </div>
            </div>

            {isMobile ? (
              <div className="px-[18px] pb-10 flex flex-col gap-2">
                <button
                  onClick={send}
                  disabled={sending}
                  className="w-full text-center bg-[#2283FF] text-white rounded-xl py-3.5 text-[14px] font-semibold shadow-[0_2px_6px_rgba(34,131,255,.35)] disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send request"}
                </button>
                <p className="text-[11px] text-[#8A98AC] text-center">Routes to the listing agent, never to the player.</p>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 px-6 pb-6">
                <p className="text-[11.5px] text-[#8A98AC]">Lands in their requests, never with the player.</p>
                <button
                  onClick={onClose}
                  className="ml-auto px-4 py-[9px] rounded-[10px] bg-white border border-[#D8E0EA] text-[13px] font-semibold text-[#33475E] hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={send}
                  disabled={sending}
                  className="px-[18px] py-[9px] rounded-[10px] bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0] disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send request"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OpportunityComposer;
